import { useState, useEffect } from "react";
import "@/App.css";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Menu, X, Mail, MapPin,
  Film, Gamepad2, Theater, Music, Clapperboard, Mic2,
  Facebook, Instagram, Twitter, Youtube, ChevronDown
} from "lucide-react";
import { Toaster, toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// TikTok Icon Component
const TikTokIcon = ({ className = "w-6 h-6" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

// SoundCloud Icon Component
const SoundCloudIcon = ({ className = "w-6 h-6" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M7 17.939h-1v-8.068c.308-.231.639-.429 1-.566v8.634zm3 0h1v-9.224c-.229.265-.443.548-.621.857l-.379-.184v8.551zm-2 0h1v-8.848c-.508-.079-.623-.05-1-.01v8.858zm-4 0h1v-7.02c-.312.458-.555.971-.692 1.535l-.308-.182v5.667zm-3-5.25c-.606.547-1 1.354-1 2.268 0 .914.394 1.721 1 2.268v-4.536zm18.879-.671c-.204-2.837-2.404-5.079-5.117-5.079-1.022 0-1.964.328-2.762.877v10.123h9.089c1.607 0 2.911-1.393 2.911-3.106 0-1.712-1.304-3.106-2.911-3.106h-.21z"/>
  </svg>
);

// Services data
const services = [
  {
    id: 1,
    title: "Film Scoring",
    description: "Original compositions for feature films, short films, and documentaries",
    icon: Film,
    image: "https://images.unsplash.com/photo-1641824997764-be92ac96c465?w=600&q=80",
    size: "large"
  },
  {
    id: 2,
    title: "Video Games",
    description: "Immersive soundtracks for gaming experiences",
    icon: Gamepad2,
    image: "https://images.unsplash.com/photo-1700300325884-10715799da7d?w=600&q=80",
    size: "medium"
  },
  {
    id: 3,
    title: "Theatre & Stage",
    description: "Musical compositions for theatrical productions",
    icon: Theater,
    image: "https://images.unsplash.com/photo-1524629802380-04f8ec63f471?w=600&q=80",
    size: "medium"
  },
  {
    id: 4,
    title: "Dance & Performance",
    description: "Custom music for dance performances and choreography",
    icon: Music,
    image: "https://images.unsplash.com/photo-1508700929628-666bc8bd84ea?w=600&q=80",
    size: "small"
  },
  {
    id: 5,
    title: "Production & Mixing",
    description: "Full production and mixing services",
    icon: Clapperboard,
    image: "https://images.unsplash.com/photo-1723778094550-a964e7b84a41?w=600&q=80",
    size: "small"
  },
  {
    id: 6,
    title: "Voice & Vocals",
    description: "Vocal arrangements and voice-over music",
    icon: Mic2,
    image: "https://images.unsplash.com/photo-1598488035139-bdbb2231ce04?w=600&q=80",
    size: "small"
  }
];

// Social links
const socialLinks = [
  { icon: Facebook, href: "https://www.facebook.com/vikkiwrightmusiccomposer", label: "Facebook" },
  { icon: Instagram, href: "https://www.instagram.com/vikkiwrightmusiccomposer/", label: "Instagram" },
  { icon: Youtube, href: "https://www.youtube.com/@vikkiwrightmusiccomposer1331", label: "YouTube" },
  { icon: SoundCloudIcon, href: "https://soundcloud.com/vikkiwrightmusiccomposer", label: "SoundCloud" },
  { icon: TikTokIcon, href: "https://www.tiktok.com/@vikki.wright.musi", label: "TikTok" },
  { icon: Twitter, href: "https://x.com/vwmusiccomposer", label: "X" },
];

// Navigation Component
const Navigation = ({ activeSection }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = ['Home', 'About', 'Portfolio', 'Services', 'Featured', 'Contact'];

  const scrollToSection = (section) => {
    const element = document.getElementById(section.toLowerCase());
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsOpen(false);
  };

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled 
          ? 'bg-gradient-to-r from-black via-zinc-900 to-black border-b border-gold/20 shadow-lg shadow-gold/5' 
          : 'bg-gradient-to-r from-black/80 via-zinc-900/80 to-black/80 backdrop-blur-sm'
      }`}
    >
      <div className="container mx-auto px-6 md:px-12 max-w-7xl">
        <div className="flex items-center justify-between h-20 relative">
          {/* Subtle gold accent line */}
          <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-gold/40 to-transparent"></div>
          
          {/* Logo */}
          <motion.a
            href="#home"
            onClick={(e) => { e.preventDefault(); scrollToSection('Home'); }}
            className="flex items-center relative z-10"
            whileHover={{ scale: 1.05 }}
            data-testid="logo-link"
          >
            <img 
              src="https://customer-assets-cm19k8pv.emergentagent.net/job_load-select/artifacts/t85vja62_3.jpg" 
              alt="Vikki Wright Music Composer Logo" 
              className="h-28 md:h-36 w-auto object-contain cursor-pointer rounded-lg"
            />
          </motion.a>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navItems.map((item) => (
              <button
                key={item}
                onClick={() => scrollToSection(item)}
                className={`text-sm tracking-[0.15em] uppercase transition-all duration-300 relative group ${
                  activeSection === item.toLowerCase() 
                    ? 'text-gold' 
                    : 'text-white/80 hover:text-gold'
                }`}
                data-testid={`nav-${item.toLowerCase()}`}
              >
                {item}
                <span className={`absolute -bottom-1 left-0 w-full h-[2px] bg-gradient-to-r from-gold/0 via-gold to-gold/0 transition-opacity duration-300 ${
                  activeSection === item.toLowerCase() ? 'opacity-100' : 'opacity-0 group-hover:opacity-50'
                }`}></span>
              </button>
            ))}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="md:hidden text-gold p-2 hover:text-white transition-colors duration-300"
            data-testid="mobile-menu-toggle"
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-gradient-to-b from-black to-zinc-900 border-t border-gold/20"
          >
            <div className="container mx-auto px-6 py-6 space-y-4">
              {navItems.map((item) => (
                <button
                  key={item}
                  onClick={() => scrollToSection(item)}
                  className={`block w-full text-left text-lg tracking-wider transition-colors duration-300 ${
                    activeSection === item.toLowerCase() 
                      ? 'text-gold' 
                      : 'text-white/80 hover:text-gold'
                  }`}
                  data-testid={`mobile-nav-${item.toLowerCase()}`}
                >
                  {item}
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};

// Hero Section
const HeroSection = () => {
  return (
    <section id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src="https://images.unsplash.com/photo-1507838153414-b4b713384a76?w=1920&q=80"
          alt="Musical composition"
          className="w-full h-full object-cover opacity-40"
        />
        <div className="absolute inset-0 hero-gradient" />
      </div>

      {/* Content */}
      <div className="relative z-10 container mx-auto px-6 md:px-12 max-w-7xl text-center">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
          className="space-y-8"
        >
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.8 }}
            className="font-accent text-xs md:text-sm tracking-[0.3em] text-gold uppercase"
          >
            Composer & Music Producer
          </motion.p>
          
          <h1 className="font-serif text-5xl md:text-7xl lg:text-8xl font-black tracking-tighter leading-none">
            <span className="block">CRAFTING</span>
            <span className="block text-gradient">SONIC</span>
            <span className="block">EMOTIONS</span>
          </h1>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.8 }}
            className="flex flex-col sm:flex-row gap-4 justify-center pt-4"
          >
            <button
              onClick={() => document.getElementById('portfolio').scrollIntoView({ behavior: 'smooth' })}
              className="bg-gold text-black hover:bg-white transition-colors duration-500 font-bold tracking-widest uppercase text-sm px-8 py-4"
              data-testid="hero-listen-btn"
            >
              Listen to My Work
            </button>
            <button
              onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
              className="border border-white/20 hover:border-gold hover:text-gold bg-transparent transition-colors duration-300 px-8 py-4 uppercase text-sm tracking-widest"
              data-testid="hero-contact-btn"
            >
              Get in Touch
            </button>
          </motion.div>
        </motion.div>

        {/* Scroll Indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5, duration: 0.8 }}
          className="absolute bottom-10 left-1/2 -translate-x-1/2"
        >
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="flex flex-col items-center gap-2 text-white/40"
          >
            <span className="text-xs tracking-widest uppercase">Scroll</span>
            <ChevronDown size={20} />
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

// About Section
const AboutSection = () => {
  return (
    <section id="about" className="py-24 md:py-32 relative">
      <div className="container mx-auto px-6 md:px-12 max-w-7xl">
        <div className="grid lg:grid-cols-2 gap-12 md:gap-20 items-center">
          {/* Profile Image - Fancy Design */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            {/* Main Profile Picture with Fancy Frame */}
            <div className="relative">
              {/* Outer Glow Effect */}
              <div className="absolute inset-0 bg-gold/20 rounded-full blur-3xl scale-105" />
              
              {/* Double Border Frame */}
              <div className="relative p-1 bg-gradient-to-br from-gold via-gold/50 to-gold rounded-full">
                <div className="p-2 bg-void rounded-full">
                  <div className="relative aspect-square overflow-hidden rounded-full border-4 border-gold/30">
                    <img
                      src="https://customer-assets.emergentagent.com/job_load-select/artifacts/fyv6shw2_Profile%20Picture.jpeg"
                      alt="Vikki Wright - Music Composer"
                      className="w-full h-full object-cover"
                    />
                    {/* Subtle Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-void/20" />
                  </div>
                </div>
              </div>
              
              {/* Decorative Corner Accents */}
              <div className="absolute -top-8 -left-8 w-16 h-16 border-l-2 border-t-2 border-gold/40" />
              <div className="absolute -bottom-8 -right-8 w-16 h-16 border-r-2 border-b-2 border-gold/40" />
              
              {/* Floating Musical Note Decorations */}
              <div className="absolute -top-4 -right-4 text-gold text-3xl opacity-40">♪</div>
              <div className="absolute -bottom-6 -left-6 text-gold text-2xl opacity-30">♫</div>
            </div>
          </motion.div>

          {/* Content */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
            className="space-y-8"
          >
            <div className="space-y-4">
              <p className="font-accent text-xs tracking-[0.3em] text-gold uppercase">About the Composer</p>
              <h2 className="font-serif text-4xl md:text-5xl font-bold tracking-tight">
                Vikki Wright
              </h2>
            </div>

            <div className="space-y-6 text-white/70 leading-relaxed">
              <p>
                I was born in Wakefield, England, in 1980. My musical journey began at an early age with 
                classical piano lessons, together with Clarinet and Flute, which laid the foundation for a 
                career as a piano teacher spanning over 20 years. In my early years I also danced many styles 
                including Ballet, Tap, Modern and performed in Musicals on numerous theatre stages alongside 
                artists such as David Essex and Paul Nicholas.
              </p>
              <p>
                Throughout my teaching career, I explored various styles through composition, a passion I&apos;ve 
                recently made my primary focus. After concluding my teaching career in 2020, I began operating 
                as Vikki Wright Music Composer.
              </p>
              <p>
                Today, I create original scores for a wide range of media, including film, television, gaming, 
                dance, stage, and theatre.
              </p>
              <p>
                I am a firm believer in &quot;Music with a Mission&quot; and frequently collaborate with my husband, 
                drummer and lyricist John Wright. Together, we&apos;ve released two singles dedicated to supporting 
                vital causes: &quot;You&apos;ll Always Be&quot; supporting Yorkshire Cancer Research and NHS Charities Together, 
                and &quot;Sunshine on Leith&quot;, a cover released in aid of the RNLI Lifeboats.
              </p>
              <p>
                My performance legacy is forged on some of the UK&apos;s most iconic stages. I&apos;ve performed at legendary 
                venues like the Cavern Club in Liverpool, Manchester Academy, The Barrowlands in Glasgow, the 2009 
                F1 Silverstone after-show, and on a Club NME night at Koko in Camden Town. During my time with the 
                band Secret Circuits, I was signed to an independent record label who released our single &quot;This Town&quot; 
                and was also represented by a top-tier London booking agency alongside artists such as Peter Green 
                (Fleetwood Mac) and The Boxer Rebellion.
              </p>
              <p>
                Completing the GEMS Film Scoring Summer Program in Valencia was an absolute milestone that culminated in 
                Spain&apos;s RTVE Symphony Orchestra recording two of my own original compositions. Standing in the studio as 
                professional musicians brought my notation to life was nothing short of surreal, filling me with an 
                overwhelming sense of pride and validation. Throughout this intense musical journey, the guidance from 
                program instructor Nigel Ashley Lees was invaluable, providing the technical foundation needed to push my 
                boundaries. The definitive highlight, however, was being personally introduced to and tutored by Hollywood 
                legend Christopher Young, whose deep insights into dramatic orchestration reshaped how I view the marriage 
                of image and sound. Leaving Spain, I feel deeply inspired, profoundly grateful, and fully equipped for the 
                next chapter of my cinematic music career.
              </p>
            </div>

            <div className="grid grid-cols-3 gap-6 pt-6">
              {[
                { id: "teaching", number: "20+", label: "Years Teaching" },
                { id: "composing", number: "5+", label: "Years Composing" },
                { id: "born", number: "Born 1980", label: "Wakefield, England" },
              ].map((stat) => (
                <div key={stat.id} className="text-center">
                  <p className="font-serif text-2xl md:text-3xl text-gold">{stat.number}</p>
                  <p className="text-xs md:text-sm text-white/50 mt-1">{stat.label}</p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Latest News Section
const LatestNewsSection = () => {
  return (
    <section id="latest-news" className="py-24 md:py-32 bg-gradient-to-b from-void to-charcoal/30">
      <div className="container mx-auto px-6 md:px-12 max-w-7xl">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12"
        >
          <p className="font-accent text-xs tracking-[0.3em] text-gold uppercase mb-4">Breaking News</p>
          <h2 className="font-serif text-4xl md:text-5xl font-bold tracking-tight">Latest News</h2>
        </motion.div>

        {/* RTVE Symphony Orchestra Recording - FEATURED NEWS */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="max-w-6xl mx-auto mb-12"
        >
          <div className="relative overflow-hidden rounded-lg border-4 border-gold/60 bg-gradient-to-br from-gold/20 to-charcoal/90 p-10 md:p-14 shadow-2xl">
            {/* FEATURED Badge */}
            <div className="absolute top-6 right-6 flex items-center gap-2 px-5 py-3 bg-gradient-to-r from-gold via-gold/80 to-gold/60 border-2 border-white/30 rounded-full shadow-lg">
              <span className="text-3xl">🎺</span>
              <span className="text-black font-extrabold text-base uppercase tracking-widest">Featured</span>
            </div>

            {/* Orchestra Photos Grid */}
            <div className="mb-10 grid grid-cols-2 md:grid-cols-4 gap-3">
              <img
                src="https://customer-assets-cm19k8pv.emergentagent.net/job_load-select/artifacts/46rlk8ih_fac29d10-f0dc-4742-adae-b724b5be2ae1.webp"
                alt="RTVE Symphony Orchestra - Bird's Eye View"
                className="w-full h-40 object-cover rounded-lg shadow-xl col-span-2"
              />
              <img
                src="https://customer-assets-cm19k8pv.emergentagent.net/job_load-select/artifacts/2yr3ssbu_a0c1d5a1-b105-4865-a240-275e443a52f5.jpg"
                alt="Vikki Wright at Recording Venue"
                className="w-full h-40 object-cover rounded-lg shadow-xl"
              />
              <img
                src="https://customer-assets-cm19k8pv.emergentagent.net/job_load-select/artifacts/8hhnrw8k_e7340eac-8a33-4146-8cf5-464e700732b0.jpg"
                alt="Monumental Theater Valencia"
                className="w-full h-40 object-cover rounded-lg shadow-xl"
              />
              <img
                src="https://customer-assets-cm19k8pv.emergentagent.net/job_load-select/artifacts/32nv2osd_Orange%20Grove%20Studio.webp"
                alt="Orange Grove Studios - Professional Mixing Console"
                className="w-full h-40 object-cover rounded-lg shadow-xl col-span-2"
              />
              <img
                src="https://customer-assets-cm19k8pv.emergentagent.net/job_load-select/artifacts/n2yhfupl_IMG_0116.webp"
                alt="Composers Entering Recording Hall"
                className="w-full h-40 object-cover rounded-lg shadow-xl"
              />
              <img
                src="https://customer-assets-cm19k8pv.emergentagent.net/job_load-select/artifacts/p93pfe2d_IMG_7569.webp"
                alt="Orchestra Rehearsal Session"
                className="w-full h-40 object-cover rounded-lg shadow-xl"
              />
              <img
                src="https://customer-assets-cm19k8pv.emergentagent.net/job_load-select/artifacts/os3v39sl_0f491183-ecfc-44af-ac10-8bf30cfd2627.jpg"
                alt="Orange Grove Studios - Mixing Session with Kjetil Grimstad"
                className="w-full h-40 object-cover rounded-lg shadow-xl col-span-2"
              />
            </div>

            {/* Achievement Banner */}
            <div className="mb-8 inline-flex items-center gap-4 px-8 py-4 bg-gradient-to-r from-gold/50 to-gold/30 border-2 border-gold/80 rounded-lg shadow-lg">
              <span className="text-4xl">🎼</span>
              <div>
                <div className="text-gold font-extrabold text-2xl">RTVE Symphony Orchestra Recording</div>
                <div className="text-gold/95 text-base">Valencia, Spain • GEMS Film Scoring Program 2026</div>
              </div>
            </div>

            {/* Main Content */}
            <div className="space-y-6">
              <h3 className="font-serif text-4xl md:text-5xl font-extrabold text-white leading-tight">
                Two Original Compositions Performed by Spain&apos;s RTVE Symphony Orchestra 🎺
              </h3>

              <div className="prose prose-invert max-w-none space-y-5">
                <p className="text-white/95 text-xl leading-relaxed font-medium">
                  Attending the <span className="text-gold font-bold">GEMS Film Scoring Summer Program in Valencia</span>, directed by <span className="text-gold font-bold">Nigel Ashley Lees</span> with guest instructor <span className="text-gold font-bold">Penka D Kouneva (LA Composer)</span> and mentored by <span className="text-gold font-bold">Dr. Juanjo Molina</span>, culminated in Spain&apos;s prestigious <span className="text-gold font-extrabold">RTVE Symphony Orchestra</span>, conducted by <span className="text-gold font-bold">Miguel Guerra</span>, performing and recording two of my original compositions.
                </p>
                <p className="text-white/90 text-lg leading-relaxed">
                  Following this incredible experience, <span className="text-gold font-semibold">Kjetil Grimstad at Orange Grove Studios in Barxeta, Spain</span>, successfully mixed and mastered the first recording and is currently mixing the second.
                </p>
                <p className="text-white/90 text-lg leading-relaxed">
                  One of these standout pieces is an <span className="text-gold font-bold">original rescore for the Drag Me to Hell (2009) Main Title</span>, created for a challenge issued by acclaimed composer <span className="text-gold font-bold">Christopher Young</span>. While video editing isn&apos;t my primary strength, I personally paired the visuals with the music to bring this intense cinematic rescore to life.
                </p>
              </div>

              {/* Call to Action */}
              <div className="pt-6 flex gap-4 flex-wrap">
                <a
                  href="#portfolio"
                  className="inline-flex items-center gap-2 px-8 py-4 bg-gold hover:bg-white text-black font-bold tracking-wider uppercase text-sm transition-all duration-300 rounded shadow-lg hover:shadow-2xl"
                >
                  <span>Watch the Rescore</span>
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </a>
              </div>
            </div>

            {/* Decorative Elements */}
            <div className="absolute bottom-0 right-0 w-96 h-96 bg-gold/10 rounded-full blur-3xl -z-10" />
            <div className="absolute top-0 left-0 w-64 h-64 bg-gold/10 rounded-full blur-3xl -z-10" />
          </div>
        </motion.div>

        {/* Christopher Young News - Latest */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="max-w-5xl mx-auto mb-12"
        >
          <div className="relative overflow-hidden rounded-lg border-2 border-gold/40 bg-gradient-to-br from-gold/10 to-charcoal/80 p-8 md:p-12">
            {/* Latest Badge */}
            <div className="absolute top-6 right-6 flex items-center gap-2 px-4 py-2 bg-gold/30 border border-gold/60 rounded-full">
              <span className="text-2xl">✨</span>
              <span className="text-gold font-bold text-sm uppercase tracking-wider">Latest</span>
            </div>

            {/* Photo */}
            <div className="mb-8">
              <img
                src="https://customer-assets.emergentagent.com/job_load-select/artifacts/k9x45a0l_Christopher%20Young%20and%20I.jpg"
                alt="Vikki Wright with Christopher Young at GEMS"
                className="w-full rounded-lg shadow-2xl"
              />
            </div>

            {/* Achievement Banner */}
            <div className="mb-6 inline-flex items-center gap-3 px-6 py-3 bg-gradient-to-r from-gold/40 to-gold/20 border border-gold/60 rounded-lg">
              <span className="text-3xl">🎬</span>
              <div>
                <div className="text-gold font-bold text-xl">Master Class with Christopher Young</div>
                <div className="text-gold/90 text-sm">GEMS Film Scoring Program 2026</div>
              </div>
            </div>

            {/* Main Content */}
            <div className="space-y-6">
              <h3 className="font-serif text-3xl md:text-4xl font-bold text-white leading-tight">
                An Unforgettable Moment with a True Master of the Craft 🙌
              </h3>

              <div className="prose prose-invert max-w-none">
                <p className="text-white/90 text-lg leading-relaxed">
                  Massive thanks to the absolute legend <span className="text-gold font-bold">Christopher Young</span> for such an amazing experience. Your encouragement for my future in film composition has me more inspired than ever to get to work! 🎹✨
                </p>
              </div>

              {/* Highlight Details */}
              <div className="bg-white/5 border border-gold/20 rounded-lg p-6">
                <div className="text-gold/80 text-xs uppercase tracking-wider mb-3">About Christopher Young</div>
                <p className="text-white/70 text-sm leading-relaxed">
                  Christopher Young is a legendary film composer known for his iconic scores including <span className="text-white font-semibold">Spider-Man 3</span>, <span className="text-white font-semibold">Hellraiser</span>, <span className="text-white font-semibold">The Exorcism of Emily Rose</span>, <span className="text-white font-semibold">Drag Me to Hell</span>, and many more. His mentorship at the GEMS Film Scoring Program represents a pivotal moment in shaping the next generation of film composers.
                </p>
              </div>
            </div>

            {/* Decorative Elements */}
            <div className="absolute bottom-0 right-0 w-64 h-64 bg-gold/5 rounded-full blur-3xl -z-10" />
            <div className="absolute top-0 left-0 w-48 h-48 bg-gold/5 rounded-full blur-3xl -z-10" />
          </div>
        </motion.div>

        {/* GEMS Family & Community Thanks */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="max-w-5xl mx-auto mb-12"
        >
          <div className="relative overflow-hidden rounded-lg border-2 border-gold/30 bg-gradient-to-br from-charcoal/80 to-void/80 p-8 md:p-12">
            {/* Photo */}
            <div className="mb-8">
              <img
                src="https://customer-assets.emergentagent.com/job_load-select/artifacts/6a2hxjot_Christopher%20Young%20with%20all.webp"
                alt="GEMS Film Scoring Program 2026 - The Composer Family"
                className="w-full rounded-lg shadow-2xl"
              />
            </div>

            {/* Achievement Banner */}
            <div className="mb-6 inline-flex items-center gap-3 px-6 py-3 bg-gradient-to-r from-gold/30 to-gold/10 border border-gold/50 rounded-lg">
              <span className="text-3xl">❤️</span>
              <div>
                <div className="text-gold font-bold text-xl">A New Musical Family</div>
                <div className="text-gold/90 text-sm">GEMS Film Scoring Program 2026</div>
              </div>
            </div>

            {/* Main Content */}
            <div className="space-y-6">
              <h3 className="font-serif text-3xl md:text-4xl font-bold text-white leading-tight">
                Thank You to an Unforgettable Community
              </h3>

              <div className="prose prose-invert max-w-none space-y-4">
                <p className="text-white/90 text-lg leading-relaxed">
                  I would like to thank everyone at <span className="text-gold font-semibold">GEMS Courses</span> for an unforgettable experience, especially <span className="text-gold font-bold">Nigel Ashley Lees</span>. I feel I have met a new family and will keep in touch with all.
                </p>
                <p className="text-white/80 text-lg leading-relaxed">
                  The group of composers on the course were so talented, inspiring, and friendly. This experience has been transformational for my journey as a film composer.
                </p>
              </div>

              {/* Call to Action Link */}
              <div className="pt-4">
                <a
                  href="https://www.gems.courses/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-gold hover:bg-white text-black font-bold tracking-wider uppercase text-sm transition-colors duration-300 rounded"
                >
                  <span>Visit GEMS Courses</span>
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </a>
              </div>
            </div>

            {/* Decorative Elements */}
            <div className="absolute bottom-0 right-0 w-64 h-64 bg-gold/5 rounded-full blur-3xl -z-10" />
            <div className="absolute top-0 left-0 w-48 h-48 bg-gold/5 rounded-full blur-3xl -z-10" />
          </div>
        </motion.div>

        {/* GEMS Selection News */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="max-w-4xl mx-auto"
        >
          <div className="relative overflow-hidden rounded-lg border-2 border-gold/30 bg-gradient-to-br from-charcoal/80 to-void/90 p-8 md:p-12">
            {/* Celebration Badge */}
            <div className="absolute top-6 right-6 flex items-center gap-2 px-4 py-2 bg-gold/20 border border-gold/40 rounded-full">
              <span className="text-2xl">🎉</span>
              <span className="text-gold font-bold text-sm uppercase tracking-wider">2026</span>
            </div>

            {/* Achievement Banner */}
            <div className="mb-6 inline-flex items-center gap-3 px-6 py-3 bg-gradient-to-r from-gold/30 to-gold/10 border border-gold/50 rounded-lg">
              <span className="text-3xl">🏆</span>
              <div>
                <div className="text-gold font-bold text-lg">Worldwide Competition Finalist</div>
                <div className="text-gold/80 text-sm">11th Place - GEMS Space Challenge</div>
              </div>
            </div>

            {/* Main Content */}
            <div className="space-y-6">
              <h3 className="font-serif text-3xl md:text-4xl font-bold text-white leading-tight">
                Selected for GEMS 2026 Film Score Summer Program in Valencia!
              </h3>

              <div className="prose prose-invert max-w-none">
                <p className="text-white/80 text-lg leading-relaxed">
                  &quot;I honestly can&apos;t believe this!! Beyond shocked but absolutely over the moon to start 2026 with this news! 
                  I&apos;ve been working on my piece for this competition for the past few months and never for once thought I&apos;d 
                  be a finalist… a worldwide competition!! I came <span className="text-gold font-bold">11th</span> and will be 
                  attending the <span className="text-gold font-semibold">GEMS 2026 Film Score Summer Program in Valencia!</span>&quot;
                </p>
              </div>

              {/* Competition Details */}
              <div className="grid md:grid-cols-2 gap-4 pt-4">
                <div className="bg-white/5 border border-white/10 rounded-lg p-5">
                  <div className="text-gold/80 text-xs uppercase tracking-wider mb-2">Competition</div>
                  <div className="text-white font-semibold">GEMS Space Challenge</div>
                  <div className="text-white/60 text-sm mt-1">Worldwide Film Score Competition</div>
                </div>
                <div className="bg-white/5 border border-white/10 rounded-lg p-5">
                  <div className="text-gold/80 text-xs uppercase tracking-wider mb-2">Achievement</div>
                  <div className="text-white font-semibold">11th Place Finalist</div>
                  <div className="text-white/60 text-sm mt-1">Selected for Valencia Summer Program</div>
                </div>
              </div>

              {/* Links */}
              <div className="flex flex-wrap gap-4 pt-6">
                <a
                  href="https://www.scorelive.london/spacechallengecompetition"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-gold hover:bg-white text-black font-bold tracking-wider uppercase text-sm transition-colors duration-300 rounded"
                >
                  <span>View Competition</span>
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </a>
                <a
                  href="https://www.gems.courses/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-6 py-3 border-2 border-gold hover:bg-gold/10 text-gold font-bold tracking-wider uppercase text-sm transition-colors duration-300 rounded"
                >
                  <span>GEMS Program</span>
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </a>
              </div>
            </div>

            {/* Decorative Elements */}
            <div className="absolute bottom-0 right-0 w-64 h-64 bg-gold/5 rounded-full blur-3xl -z-10" />
            <div className="absolute top-0 left-0 w-48 h-48 bg-gold/5 rounded-full blur-3xl -z-10" />
          </div>
        </motion.div>

        {/* Additional Context */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.5 }}
          className="mt-12 text-center"
        >
          <p className="text-white/60 max-w-3xl mx-auto text-lg leading-relaxed">
            This achievement demonstrates exceptional compositional talent on an international stage. 
            The GEMS Film Score Summer Program in Valencia offers world-class training with industry professionals, 
            further expanding expertise in cinematic composition.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

// Studio Gallery Section
const StudioSection = () => {
  const studioImages = [
    {
      id: 1,
      url: "https://customer-assets.emergentagent.com/job_load-select/artifacts/cghbgb6u_IMG_2233.jpg",
      title: "Piano & Composition",
      description: "Where melodies come to life"
    },
    {
      id: 2,
      url: "https://customer-assets.emergentagent.com/job_load-select/artifacts/a88b66o1_IMG_2228.jpg",
      title: "Drum Recording",
      description: "Professional acoustic treatment"
    },
    {
      id: 3,
      url: "https://customer-assets.emergentagent.com/job_load-select/artifacts/g3mdlvq5_IMG_2229.jpg",
      title: "Guitar Collection",
      description: "Diverse tonal palette"
    },
    {
      id: 4,
      url: "https://customer-assets.emergentagent.com/job_load-select/artifacts/scqpte43_IMG_2231.jpg",
      title: "Audio Interface",
      description: "Professional recording equipment"
    },
    {
      id: 5,
      url: "https://customer-assets.emergentagent.com/job_load-select/artifacts/5z2vii27_IMG_2232.jpg",
      title: "Mixing Console",
      description: "Focusrite Pro Tools"
    },
    {
      id: 6,
      url: "https://customer-assets.emergentagent.com/job_load-select/artifacts/3fu7d360_IMG_2230.jpg",
      title: "Vocal Recording",
      description: "Studio-grade acoustics"
    }
  ];

  return (
    <section id="studio" className="py-24 md:py-32 bg-charcoal/30">
      <div className="container mx-auto px-6 md:px-12 max-w-7xl">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <p className="font-accent text-xs tracking-[0.3em] text-gold uppercase mb-4">Behind the Music</p>
          <h2 className="font-serif text-4xl md:text-5xl font-bold tracking-tight">The Studio</h2>
          <p className="mt-4 text-white/60 max-w-2xl mx-auto">
            A glimpse into the creative space where sonic emotions are crafted
          </p>
        </motion.div>

        {/* Studio Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {studioImages.map((image, index) => (
            <motion.div
              key={image.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="group relative aspect-square overflow-hidden bg-charcoal/50 border border-white/5 hover:border-gold/30 transition-all duration-500"
            >
              <img
                src={image.url}
                alt={image.title}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-void via-void/50 to-transparent opacity-60 group-hover:opacity-80 transition-opacity duration-500" />
              
              {/* Overlay Content */}
              <div className="absolute inset-0 flex flex-col justify-end p-6 translate-y-4 group-hover:translate-y-0 transition-transform duration-500">
                <h3 className="font-serif text-xl md:text-2xl font-bold text-gold mb-2">
                  {image.title}
                </h3>
                <p className="text-white/70 text-sm opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                  {image.description}
                </p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Studio Info */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.5 }}
          className="mt-16 text-center"
        >
          <p className="text-white/60 max-w-3xl mx-auto leading-relaxed">
            Our professional recording studio is equipped with industry-standard equipment, including professional 
            audio interfaces, studio-grade acoustics, and a diverse collection of instruments. Every detail is 
            carefully crafted to ensure the highest quality production for your project.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

// Services Section with Bento Grid
const ServicesSection = () => {
  return (
    <section id="services" className="py-24 md:py-32 bg-charcoal/50">
      <div className="container mx-auto px-6 md:px-12 max-w-7xl">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <p className="font-accent text-xs tracking-[0.3em] text-gold uppercase mb-4">What I Offer</p>
          <h2 className="font-serif text-4xl md:text-5xl font-bold tracking-tight">Services</h2>
        </motion.div>

        {/* Bento Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((service, index) => (
            <motion.div
              key={service.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className={`service-card group relative rounded-sm overflow-hidden ${
                service.size === 'large' ? 'md:col-span-2 md:row-span-2' : (
                  service.size === 'medium' ? 'md:row-span-2' : ''
                )
              }`}
              data-testid={`service-card-${service.id}`}
            >
              <img
                src={service.image}
                alt={service.title}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
              />
              <div className="relative z-10 p-6 md:p-8 h-full min-h-[280px] md:min-h-[320px] flex flex-col justify-end">
                <div className="flex items-center gap-3 mb-3">
                  <service.icon className="w-6 h-6 text-gold" />
                  <h3 className="font-serif text-xl md:text-2xl font-bold">{service.title}</h3>
                </div>
                <p className="text-white/70 text-sm md:text-base leading-relaxed">
                  {service.description}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Portfolio Section with Embedded Players
const PortfolioSection = () => {
  return (
    <section id="portfolio" className="py-24 md:py-32">
      <div className="container mx-auto px-6 md:px-12 max-w-7xl">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <p className="font-accent text-xs tracking-[0.3em] text-gold uppercase mb-4">Listen</p>
          <h2 className="font-serif text-4xl md:text-5xl font-bold tracking-tight">Portfolio</h2>
          <p className="mt-4 text-white/60 max-w-2xl mx-auto">
            A selection of original compositions showcasing the range and depth of my musical work.
          </p>
        </motion.div>

        {/* YouTube Embedded Videos */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="max-w-5xl mx-auto mb-12"
        >
          <div className="grid md:grid-cols-2 gap-6">
            {/* YouTube Channel Embed */}
            <div className="bg-charcoal/50 border border-white/10 p-4 rounded-sm" data-testid="youtube-embed-1">
              <div className="aspect-video">
                <iframe 
                  width="100%" 
                  height="100%"
                  src="https://www.youtube.com/embed/videoseries?list=UUZd1RsOqplc8JF7zprrhqiA"
                  title="Vikki Wright Music - YouTube Playlist"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="rounded"
                />
              </div>
              <p className="text-sm text-white/50 mt-3 text-center">Latest uploads from YouTube</p>
            </div>

            {/* SoundCloud Embed */}
            <div className="bg-charcoal/50 border border-white/10 p-4 rounded-sm" data-testid="soundcloud-embed">
              <div className="aspect-video flex flex-col">
                <iframe 
                  width="100%" 
                  height="100%"
                  scrolling="no" 
                  frameBorder="no" 
                  allow="autoplay"
                  title="Vikki Wright Music on SoundCloud"
                  src="https://w.soundcloud.com/player/?url=https%3A//soundcloud.com/user-723091766&color=%23d4af37&auto_play=false&hide_related=true&show_comments=false&show_user=true&show_reposts=false&show_teaser=false&visual=true"
                  className="rounded flex-1"
                />
              </div>
              <p className="text-sm text-white/50 mt-3 text-center">Stream on SoundCloud</p>
            </div>
          </div>
        </motion.div>

        {/* Music Categories */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="max-w-4xl mx-auto"
        >
          <div className="grid md:grid-cols-3 gap-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="text-center p-6 bg-charcoal/30 border border-white/5 hover:border-gold/20 transition-colors"
            >
              <Film className="w-8 h-8 text-gold mx-auto mb-3" />
              <h4 className="font-serif text-lg mb-2">Film Scores</h4>
              <p className="text-sm text-white/50">Epic orchestral compositions for cinema</p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.4 }}
              className="text-center p-6 bg-charcoal/30 border border-white/5 hover:border-gold/20 transition-colors"
            >
              <Gamepad2 className="w-8 h-8 text-gold mx-auto mb-3" />
              <h4 className="font-serif text-lg mb-2">Game Music</h4>
              <p className="text-sm text-white/50">Immersive soundtracks for gaming</p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.5 }}
              className="text-center p-6 bg-charcoal/30 border border-white/5 hover:border-gold/20 transition-colors"
            >
              <Theater className="w-8 h-8 text-gold mx-auto mb-3" />
              <h4 className="font-serif text-lg mb-2">Stage & Theatre</h4>
              <p className="text-sm text-white/50">Dramatic scores for live performances</p>
            </motion.div>
          </div>
        </motion.div>

        {/* More Platforms */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="text-center mt-12"
        >
          <p className="text-white/40 text-sm mb-4">Follow for more music</p>
          <div className="flex justify-center gap-6 flex-wrap">
            <a
              href="https://soundcloud.com/user-723091766"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-white/60 hover:text-gold transition-colors"
              data-testid="soundcloud-link"
            >
              <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                <path d="M1.175 12.225c-.051 0-.094.046-.101.1l-.233 2.154.233 2.105c.007.058.05.098.101.098.05 0 .09-.04.099-.098l.255-2.105-.27-2.154c-.009-.06-.052-.1-.084-.1zm-.899.828c-.06 0-.091.037-.104.094L0 14.479l.165 1.308c.014.057.045.094.09.094s.089-.037.099-.094l.19-1.308-.19-1.332c-.01-.057-.044-.094-.078-.094zm1.83-1.229c-.06 0-.12.04-.12.1l-.21 2.563.225 2.458c0 .06.045.1.105.1.074 0 .12-.04.12-.1l.24-2.458-.24-2.563c0-.06-.03-.1-.12-.1zm.945-.089c-.075 0-.135.06-.15.135l-.193 2.64.21 2.544c.016.077.075.138.149.138.075 0 .135-.061.15-.138l.24-2.544-.24-2.64c-.015-.075-.06-.135-.166-.135zm1.155.36c-.005-.09-.075-.149-.159-.149-.09 0-.158.06-.164.149l-.217 2.43.2 2.563c.005.09.075.157.159.157.074 0 .148-.068.148-.158l.227-2.563-.194-2.429zm.809-1.709c-.101 0-.18.09-.18.181l-.21 3.957.227 2.563c0 .09.08.164.165.164.09 0 .164-.074.178-.164l.24-2.563-.239-3.957c-.014-.091-.089-.181-.181-.181zm.959-.914c-.105 0-.195.09-.21.195l-.194 4.872.21 2.538c.015.105.09.18.21.18.105 0 .18-.075.195-.18l.24-2.538-.24-4.872c-.015-.105-.074-.195-.211-.195zm1.125.238c-.12 0-.221.1-.221.209l-.194 4.631.21 2.51c0 .119.09.209.21.209.12 0 .209-.09.224-.209l.24-2.51-.24-4.631c-.015-.109-.105-.209-.229-.209zm.96-.329c-.135 0-.24.105-.255.24l-.179 4.681.195 2.48c.015.135.105.24.24.24.135 0 .24-.105.254-.24l.226-2.48-.226-4.681c-.014-.135-.119-.24-.255-.24zm1.08-.584c-.15 0-.27.12-.285.27l-.165 5.008.18 2.438c.016.15.12.27.271.27.15 0 .27-.12.285-.27l.21-2.438-.21-5.008c-.015-.15-.135-.27-.286-.27zm1.155.029c-.165 0-.3.135-.314.285l-.15 4.694.165 2.408c.014.15.135.285.3.285.164 0 .285-.135.3-.285l.18-2.408-.18-4.694c-.015-.15-.135-.285-.301-.285zm1.064-.285c-.18 0-.314.135-.329.315l-.14 4.694.155 2.378c.015.165.15.315.314.315.18 0 .314-.15.329-.315l.181-2.378-.181-4.694c-.015-.18-.149-.315-.329-.315zm1.095-.314c-.196 0-.346.149-.361.345l-.118 4.724.133 2.354c.015.18.15.33.346.33.18 0 .331-.15.346-.33l.165-2.354-.165-4.724c-.029-.196-.166-.345-.346-.345zm1.109-.314c-.21 0-.375.164-.389.375l-.105 4.754.12 2.324c.014.195.164.359.374.359.21 0 .361-.164.39-.359l.135-2.324-.135-4.754c-.029-.211-.18-.375-.39-.375zm2.125-.764c-.105-.06-.225-.09-.345-.09-.12 0-.24.03-.345.09-.21.119-.346.345-.346.599l-.12 5.459.135 2.324c.014.195.164.36.374.36.21 0 .361-.165.375-.36l.151-2.324-.151-5.459c0-.254-.135-.48-.346-.599zm1.155.389c-.21 0-.375.165-.39.375l-.104 5.205.119 2.294c.015.21.18.375.375.375.21 0 .375-.165.39-.375l.134-2.294-.134-5.205c-.015-.21-.18-.375-.39-.375zm1.125.15c-.224 0-.404.18-.419.404l-.09 5.056.105 2.264c.015.225.195.404.404.404.21 0 .405-.179.42-.404l.119-2.264-.119-5.056c-.015-.224-.195-.404-.42-.404zm5.284 4.14c-.255 0-.495.045-.72.135-.149-1.68-1.559-3.004-3.284-3.004-.449 0-.885.09-1.274.239-.15.06-.194.12-.194.24v6.144c0 .12.09.225.21.24l5.262.015c1.155 0 2.085-.93 2.085-2.085 0-1.124-.93-2.054-2.085-2.054v.13z"/>
              </svg>
              <span>SoundCloud</span>
            </a>
            <a
              href="https://open.spotify.com/user/ntjnm7nxiemz1e2df2hdalv4u"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-white/60 hover:text-gold transition-colors"
              data-testid="spotify-link"
            >
              <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/>
              </svg>
              <span>Spotify</span>
            </a>
            <a
              href="https://www.youtube.com/channel/UCZd1RsOqplc8JF7zprrhqiA"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-white/60 hover:text-gold transition-colors"
              data-testid="youtube-link"
            >
              <Youtube className="w-6 h-6" />
              <span>YouTube</span>
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// Work Examples Section
const WorkExamplesSection = () => {
  const workExamples = [
    {
      id: 1,
      title: "Cascada de Cuerdas (Waterfall of Strings)",
      type: "Original Composition - RTVE Orchestra",
      description: "Dedicated to the memory of my dear father-in-law, whose unconditional support for our musical journey was second to none. Recorded live by the phenomenal RTVE National Symphonic Orchestra during GEMS Film Scoring in Spain. A symphony for a lifetime flowing by...",
      videoUrl: "https://customer-assets-cm19k8pv.emergentagent.net/job_load-select/artifacts/315ae76c12c649fc_Cascada%20de%20Cuerdas%20Website.mp4",
      poster: "https://customer-assets-cm19k8pv.emergentagent.net/job_load-select/artifacts/42b371533157694c_Title%20Page%20Cascada%20de%20Cuerdas.jpg",
      year: "2026"
    },
    {
      id: 2,
      title: "Drag Me to Hell - Main Title Rescore",
      type: "Orchestral Rescore",
      description: "Original rescore performed by Spain's RTVE Symphony Orchestra. Created for a challenge by Christopher Young, mixed by Kjetil Grimstad at Orange Grove Studios",
      videoUrl: "https://customer-assets-cm19k8pv.emergentagent.net/job_load-select/artifacts/gn7g92wz_Drag%20me%20to%20Hell.mp4",
      poster: "https://customer-assets-cm19k8pv.emergentagent.net/job_load-select/artifacts/k63qzeff_Image.jpeg",
      year: "2026"
    },
    {
      id: 3,
      title: "Data Sonification - GEMS Competition Winner",
      type: "Original Composition",
      description: "The winning piece that secured my place at the July 2026 GEMS Film Scoring course in Valencia. Redefines data as a living catalyst for human emotion - powerful, positive, and deeply expressive",
      videoUrl: "https://customer-assets-cm19k8pv.emergentagent.net/job_load-select/artifacts/j6n7v5ie_Data%20Sonification.mp4%20%28Lower%20res%29.mp4",
      poster: "https://customer-assets-cm19k8pv.emergentagent.net/job_load-select/artifacts/ua2l6v3b_Data%20Sonification%20title%20page.jpg",
      year: "2026"
    },
    {
      id: 4,
      title: "Whispers In The Dark - Credit to @TheCueTube",
      type: "Psychological Horror Rescore",
      description: "Created for The Cue Tube's 2024 Halloween special. An exercise in psychological horror, translating Jack's internal guilt and shattering sanity into a tangible sonic environment",
      videoUrl: "https://customer-assets-cm19k8pv.emergentagent.net/job_load-select/artifacts/g1ha4ff0_Whispers%20In%20The%20Dark.mp4",
      poster: "https://customer-assets-cm19k8pv.emergentagent.net/job_load-select/artifacts/2lvi9fhb_Whispers%20In%20The%20Dark.jpg",
      year: "2026"
    },
    {
      id: 5,
      title: "Vesper Rescore - Credit to The Cue Tube",
      type: "Rescore",
      description: "A thriller rescore showcasing dramatic tension and atmospheric storytelling",
      videoUrl: "https://customer-assets-cm19k8pv.emergentagent.net/job_load-select/artifacts/6b6ok2dk_Vesper%20Smaller%20File.mp4",
      poster: "https://customer-assets-cm19k8pv.emergentagent.net/job_load-select/artifacts/7cc72vqc_FREE%20TRIAL-3.png",
      year: "2026"
    },
    {
      id: 6,
      title: "Vectr Rescore - Credit to The Cue Tube",
      type: "Rescore",
      description: "A rescore showcasing diverse compositional skills and cinematic storytelling through music",
      videoUrl: "https://customer-assets-cm19k8pv.emergentagent.net/job_load-select/artifacts/74dn3zk7_Vectr%20Rescore%20Vid%20and%20Music.mp4",
      poster: "https://customer-assets-cm19k8pv.emergentagent.net/job_load-select/artifacts/4dltqg4m_mq1.webp",
      year: "2026"
    },
    {
      id: 7,
      title: "Being Alive (New - Rescore, 2026) - Credit to The Cue Tube",
      type: "Rescore",
      description: "Original rescore of 'Being Alive' featuring video by The Cue Tube",
      videoUrl: "https://customer-assets-cm19k8pv.emergentagent.net/job_load-select/artifacts/b7jp70p4_Being%20Alive%20for%20TikTok.mp4",
      poster: "https://customer-assets-cm19k8pv.emergentagent.net/job_load-select/artifacts/fszebqeq_Image%20for%20Being%20Alive.jpeg",
      year: "2026"
    },
    {
      id: 8,
      title: "Halloween 2023 Rescore - Credit to @TheCueTube",
      type: "Rescore",
      description: "Watch till the end... 🤡",
      videoUrl: "https://customer-assets-cm19k8pv.emergentagent.net/job_load-select/artifacts/da3h3069_Halloween%202023%20Rescore%20Final.mp4",
      poster: "https://customer-assets-cm19k8pv.emergentagent.net/job_load-select/artifacts/x2l40z7t_image.png",
      year: "2026"
    },
    {
      id: 9,
      title: "Rise of the Sunken Sun",
      type: "Original Composition",
      description: "A dramatic orchestral composition blending traditional Persian melodic structures with powerful Western symphonic arrangements, creating a vivid sonic landscape driven by sweeping string sections and thunderous timpani.",
      videoUrl: "https://customer-assets.emergentagent.com/job_load-select/artifacts/r71rcucn_Rise%20of%20the%20Sunken%20Sun.mp4",
      poster: "https://customer-assets.emergentagent.com/job_load-select/artifacts/gklqoqps_Rise%20of%20the%20Sunken%20Sun-Cover.jpg",
      year: "2026"
    },
    {
      id: 10,
      title: "Cascada de Cuerda (Waterfall of Strings) - Original Recording",
      type: "Original Composition",
      description: "A composition blending stringed instruments intertwined with the natural acoustics of cascading water, mirroring each other's movement, rhythm, and texture.",
      videoUrl: "https://customer-assets.emergentagent.com/job_load-select/artifacts/0prv2jap_Cascada%20de%20Cuerda%20%28Waterfall%20of%20Strings%29.mp4",
      poster: "https://customer-assets.emergentagent.com/job_load-select/artifacts/en1eo5qp_Cascada%20de%20Cuerda%20%28Waterfall%20of%20Strings%29-Cover.jpg",
      year: "2026"
    },
    {
      id: 11,
      title: "War Flower",
      type: "Original Composition",
      description: "An immersive, tense, atmospheric war scene that blends intense action with a truly positive resolution.",
      videoUrl: "https://customer-assets.emergentagent.com/job_load-select/artifacts/o78gv993_War%20Flower%20Final.mp4",
      poster: "https://customer-assets.emergentagent.com/job_load-select/artifacts/qg8dhoge_War%20Flower%20Final-Cover.jpg",
      year: "2026"
    },
    {
      id: 12,
      title: "The Blaze Star",
      type: "Original Composition",
      description: "Superhero theme with 80's synth pop vibes! Warm, analog synth textures and melodic, danceable beats create optimistic energy mixed with melancholic depth, power and cinematic escapism.",
      videoUrl: "https://customer-assets.emergentagent.com/job_load-select/artifacts/2gl2t6do_The%20Blaze%20Star%20Video%20and%20Music.mp4",
      poster: "https://customer-assets.emergentagent.com/job_load-select/artifacts/jei28ucw_The%20Blaze%20Star%20Video%20and%20Music-Cover.jpg",
      year: "2026"
    },
    {
      id: 13,
      title: "Inside the Evil",
      type: "Original Composition",
      description: "Too much to say about this composition... Too much evil in the world... Too many fights to fight... Deep emotions... Relate...",
      videoUrl: "https://customer-assets.emergentagent.com/job_load-select/artifacts/xektnr1x_Inside%20the%20Evil%20Final.mp4",
      poster: "https://customer-assets.emergentagent.com/job_load-select/artifacts/ou54mze0_Inside%20the%20Evil%20Final-Cover.jpg",
      year: "2026"
    },
    {
      id: 14,
      title: "Wrapped in a Velvet Night",
      type: "Original Composition",
      description: "Do you struggle to fall into a deep sleep? I've composed this piece of restful music with this in mind!",
      videoUrl: "https://customer-assets.emergentagent.com/job_load-select/artifacts/ie44qvcg_Wrapped%20in%20a%20Velvet%20Night.mp4",
      poster: "https://customer-assets.emergentagent.com/job_load-select/artifacts/fchp54e1_Wrapped%20in%20a%20Velvet%20Night-Cover.jpg",
      year: "2026"
    },
    {
      id: 15,
      title: "Quantum",
      type: "Original Composition",
      description: "Original composition inspired by the film 'Drive' featuring Ryan Gosling. Visuals compiled from Pixabay.",
      videoUrl: "https://customer-assets.emergentagent.com/job_load-select/artifacts/u4p85u3u_Quantum%20Video.mov",
      poster: "https://customer-assets.emergentagent.com/job_load-select/artifacts/4pqplhlx_Quantum%20Video-Cover.jpg",
      year: "2020"
    },
    {
      id: 16,
      title: "Supremacy of the Mind",
      type: "Original Composition",
      description: "Epic orchestral composition written from the mindset of a heroic superhero. Inspired by characters like Captain America, Thor, and Wonder Woman - a powerful soundtrack for a battle scene. Visuals compiled from Pixabay.",
      videoUrl: "https://customer-assets.emergentagent.com/job_load-select/artifacts/vyt7j8fk_Supremacy%20of%20the%20Mind.mov",
      poster: "https://customer-assets.emergentagent.com/job_load-select/artifacts/lns0gylo_Supremacy%20of%20the%20Mind%20Pic.jpg",
      year: "2020"
    },
    {
      id: 17,
      title: "Walter",
      type: "Film Score",
      description: "Film extract used with permission from Lorenzo Fresta",
      videoUrl: "https://customer-assets.emergentagent.com/job_load-select/artifacts/6ohrditb_Walter%20iMovie%20.mp4",
      poster: "https://customer-assets.emergentagent.com/job_load-select/artifacts/pdlsi0ji_Walter.jpg",
      year: "2026",
      testimonials: [
        "This is a charming piece with a distinct personality. You have a clear musical voice, and it is fun to listen to.",
        "The moment the little flower blooms is beautifully handled. It has real magic to it.",
        "The melody and orchestration from 2:25 onward are light and lovely. They capture the feeling of flying through the night sky really well.",
        "You have a strong musical voice, and the piece has its own internal logic. Keep going!"
      ],
      feedback: "Indie Film Music Contest"
    },
    {
      id: 18,
      title: "When The Clapping Stops",
      type: "Film Score",
      description: "Film extract used with permission from Cameron Perry",
      videoUrl: "https://customer-assets.emergentagent.com/job_load-select/artifacts/wjrkh73j_%20When%20The%20CLapping%20Stops.mov",
      poster: "https://customer-assets.emergentagent.com/job_load-select/artifacts/dppuk39z_When%20the%20Clapping%20Stops.jpg",
      year: "2026"
    },
    {
      id: 19,
      title: "Buñuel in the Labyrinth of the Turtles",
      type: "Film Score",
      description: "Original score composed for a scene from Buñuel in the Labyrinth of the Turtles. Top 5 finalist in Abbey Road Studios Composer Competition.",
      videoUrl: "https://customer-assets.emergentagent.com/job_load-select/artifacts/0qm6ikup_Bruneul.mp4",
      poster: "https://customer-assets.emergentagent.com/job_load-select/artifacts/b5hxxrfv_Bunuel.jpeg",
      year: "2020",
      award: "Abbey Road Studios - Top 5 Finalist"
    }
  ];

  return (
    <section id="portfolio" className="py-24 md:py-32 bg-charcoal/50">
      <div className="container mx-auto px-6 md:px-12 max-w-7xl">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <p className="font-accent text-xs tracking-[0.3em] text-gold uppercase mb-4">My Compositions</p>
          <h2 className="font-serif text-4xl md:text-5xl font-bold tracking-tight">Portfolio</h2>
          <p className="mt-4 text-white/60 max-w-2xl mx-auto">
            Original scores and compositions created for film, television, and multimedia projects
          </p>
        </motion.div>

        {/* Work Examples Grid */}
        <div className="max-w-6xl mx-auto space-y-12">
          {workExamples.map((work, index) => (
            <motion.div
              key={work.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.2 }}
              className="bg-charcoal/50 border border-white/10 rounded-lg overflow-hidden hover:border-gold/30 transition-all duration-500"
            >
              {/* Video Player */}
              <div className="relative aspect-video bg-void">
                <video
                  controls
                  controlsList="nodownload"
                  className="w-full h-full object-contain"
                  preload="auto"
                  playsInline
                  muted={false}
                  poster={work.poster || undefined}
                >
                  <source src={work.videoUrl} type="video/mp4" />
                  <source src={work.videoUrl} type="video/quicktime" />
                  <source src={work.videoUrl} type="video/mov" />
                  Your browser does not support the video tag.
                </video>
              </div>

              {/* Work Details */}
              <div className="p-6 md:p-8">
                <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
                  <div className="flex items-center gap-3">
                    <span className="inline-block px-4 py-1 bg-gold/20 text-gold text-xs uppercase tracking-wider rounded-full border border-gold/30">
                      {work.type}
                    </span>
                    <span className="text-white/40 text-sm">{work.year}</span>
                  </div>
                </div>

                {/* Award Badge */}
                {work.award && (
                  <div className="mb-4 inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-gold/20 to-gold/10 border border-gold/40 rounded">
                    <span className="text-gold text-lg">🏆</span>
                    <span className="text-gold font-semibold text-sm">{work.award}</span>
                  </div>
                )}

                <h3 className="font-serif text-2xl md:text-3xl font-bold text-white mb-3">
                  {work.title}
                </h3>

                <p className="text-white/70 text-base leading-relaxed">
                  {work.description}
                </p>

                {/* Professional Feedback/Testimonials */}
                {work.testimonials && work.feedback && (
                  <div className="mt-6 space-y-4">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-gold text-lg">⭐</span>
                      <span className="text-gold font-semibold text-sm uppercase tracking-wider">{work.feedback}</span>
                    </div>
                    <div className="space-y-3">
                      {work.testimonials.map((quote, idx) => (
                        <div key={`${work.id}-testimonial-${idx}`} className="pl-4 border-l-2 border-gold/30 bg-white/5 p-3 rounded-r">
                          <p className="text-white/80 text-sm italic leading-relaxed">
                            &quot;{quote}&quot;
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          ))}
        </div>

        {/* Call to Action */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.5 }}
          className="mt-16 text-center"
        >
          <p className="text-white/60 mb-6 max-w-2xl mx-auto">
            Interested in original music for your project? Let&apos;s discuss how we can bring your vision to life through the power of music.
          </p>
          <button
            onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
            className="bg-gold hover:bg-white text-black font-bold tracking-widest uppercase text-sm px-8 py-4 transition-colors duration-500"
          >
            Start a Project
          </button>
        </motion.div>
      </div>
    </section>
  );
};

// Featured Projects & Press Section
const FeaturedSection = () => {
  const featuredProjects = [
    {
      id: 1,
      title: "NHS Heroes Charity Single",
      description: "\"You'll Always Be\" - A song released to raise funds for NHS Charities Together during COVID-19, originally written in memory of Vikki's aunt Rosemarie Booth.",
      source: "Wakefield Express",
      date: "May 2020",
      link: "https://www.wakefieldexpress.co.uk/news/people/wakefield-songwriting-parents-tune-to-raise-cash-for-nhs-health-heroes-2848784",
      type: "charity"
    },
    {
      id: 2,
      title: "Sunshine on Leith - RNLI Charity Cover",
      description: "A cover of The Proclaimers' \"Sunshine on Leith\" by EOSR (Evolution of Sound Recordings) to raise proceeds for RNLI South Queensferry in memory of Andrew Hardie.",
      source: "The Proclaimers Official",
      date: "March 2014",
      link: "https://the.proclaimers.co.uk/sunshine-on-leith-covered-by-eosr-to-raise-proceeds-for-rnli-south-queensferry",
      type: "charity"
    },
    {
      id: 3,
      title: "The Proclaimers - Latest News Feature",
      description: "Featured on The Proclaimers official website for charitable music contributions.",
      source: "The Proclaimers",
      date: "2014",
      link: "https://the.proclaimers.co.uk/latest-news",
      type: "press"
    },
    {
      id: 4,
      title: "In Loving Memory - Andrew Hardie",
      description: "A tribute and memorial page honoring Andrew Hardie, whose memory inspired the charity single release.",
      source: "MuchLoved Memorial",
      date: "2013",
      link: "https://andrew-hardie.muchloved.com/",
      type: "memorial"
    }
  ];

  return (
    <section id="featured" className="py-24 md:py-32 bg-charcoal/30">
      <div className="container mx-auto px-6 md:px-12 max-w-7xl">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <p className="font-accent text-xs tracking-[0.3em] text-gold uppercase mb-4">Making a Difference</p>
          <h2 className="font-serif text-4xl md:text-5xl font-bold tracking-tight">Featured Projects & Press</h2>
          <p className="mt-4 text-white/60 max-w-2xl mx-auto">
            Music created with purpose - raising funds for charities close to our hearts.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-6">
          {featuredProjects.map((project, index) => (
            <motion.a
              key={project.id}
              href={project.link}
              target="_blank"
              rel="noopener noreferrer"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="group block p-6 md:p-8 bg-charcoal/50 border border-white/5 hover:border-gold/30 transition-all duration-500"
              data-testid={`featured-project-${project.id}`}
            >
              <div className="flex items-start justify-between mb-4">
                <span className={`text-xs uppercase tracking-wider px-3 py-1 rounded-full ${
                  project.type === 'charity' ? 'bg-gold/20 text-gold' : (
                    project.type === 'press' ? 'bg-white/10 text-white/70' : 'bg-bronze/20 text-bronze'
                  )
                }`}>
                  {project.type === 'charity' ? 'Charity Single' : (
                    project.type === 'press' ? 'Press Feature' : 'Memorial Tribute'
                  )}
                </span>
                <span className="text-sm text-white/40">{project.date}</span>
              </div>
              
              <h3 className="font-serif text-xl md:text-2xl font-bold mb-3 group-hover:text-gold transition-colors">
                {project.title}
              </h3>
              
              <p className="text-white/60 text-sm md:text-base leading-relaxed mb-4">
                {project.description}
              </p>
              
              <div className="flex items-center justify-between">
                <span className="text-sm text-white/40">Source: {project.source}</span>
                <span className="text-gold text-sm group-hover:translate-x-1 transition-transform inline-flex items-center gap-1">
                  Read More 
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </span>
              </div>
            </motion.a>
          ))}
        </div>

        {/* Charity Message */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.5 }}
          className="mt-12 text-center p-8 border border-gold/20 bg-gold/5"
        >
          <p className="text-white/70 italic">
            &quot;We feel lucky to be able to create music that makes a difference. These projects represent our commitment to using music for positive change in our community.&quot;
          </p>
          <p className="text-gold mt-4 font-serif">— Vikki & John Wright</p>
        </motion.div>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    project_type: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      toast.success('Message sent successfully! I\'ll get back to you soon.');
      setFormData({ name: '', email: '', subject: '', project_type: '', message: '' });
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 md:py-32 relative">
      {/* Background */}
      <div className="absolute inset-0 opacity-20">
        <img
          src="https://images.unsplash.com/photo-1511379938547-c1f69419868d?w=1920&q=80"
          alt="Background"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-void/90" />
      </div>

      <div className="relative z-10 container mx-auto px-6 md:px-12 max-w-7xl">
        <div className="grid lg:grid-cols-2 gap-12 md:gap-20">
          {/* Left Side - Info */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="space-y-8"
          >
            <div className="space-y-4">
              <p className="font-accent text-xs tracking-[0.3em] text-gold uppercase">Get in Touch</p>
              <h2 className="font-serif text-4xl md:text-5xl font-bold tracking-tight">Let&apos;s Create Together</h2>
            </div>

            <p className="text-white/70 leading-relaxed max-w-md">
              Have a project in mind? I&apos;d love to hear about it. Whether you need music for film, 
              games, theatre, or any creative endeavor, let&apos;s discuss how I can help bring your 
              vision to life.
            </p>

            <div className="space-y-4 pt-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-gold/10 rounded-full">
                  <Mail className="w-5 h-5 text-gold" />
                </div>
                <div>
                  <p className="text-sm text-white/50">Email</p>
                  <p className="text-white">contact@vikkiwrightmusic.com</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-gold/10 rounded-full">
                  <MapPin className="w-5 h-5 text-gold" />
                </div>
                <div>
                  <p className="text-sm text-white/50">Location</p>
                  <p className="text-white">England, United Kingdom</p>
                </div>
              </div>
            </div>

            {/* Social Links */}
            <div className="flex gap-4 pt-4">
              {socialLinks.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 flex items-center justify-center border border-white/10 hover:border-gold hover:bg-gold/10 transition-all duration-300"
                  aria-label={social.label}
                  data-testid={`social-${social.label.toLowerCase()}`}
                >
                  <social.icon className="w-5 h-5 text-white/70 hover:text-gold" />
                </a>
              ))}
            </div>
          </motion.div>

          {/* Right Side - Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm text-white/50 mb-2">Your Name</label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-3 bg-charcoal/50 border border-white/10 focus:border-gold/50 outline-none transition-colors text-white"
                    placeholder="John Doe"
                    data-testid="contact-name"
                  />
                </div>
                <div>
                  <label className="block text-sm text-white/50 mb-2">Email Address</label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-4 py-3 bg-charcoal/50 border border-white/10 focus:border-gold/50 outline-none transition-colors text-white"
                    placeholder="john@example.com"
                    data-testid="contact-email"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm text-white/50 mb-2">Project Type</label>
                <select
                  value={formData.project_type}
                  onChange={(e) => setFormData({ ...formData, project_type: e.target.value })}
                  className="w-full px-4 py-3 bg-charcoal/50 border border-white/10 focus:border-gold/50 outline-none transition-colors text-white"
                  data-testid="contact-project-type"
                >
                  <option value="">Select a project type</option>
                  <option value="film">Film Scoring</option>
                  <option value="videogame">Video Game Music</option>
                  <option value="theatre">Theatre & Stage</option>
                  <option value="dance">Dance & Performance</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-sm text-white/50 mb-2">Subject</label>
                <input
                  type="text"
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  className="w-full px-4 py-3 bg-charcoal/50 border border-white/10 focus:border-gold/50 outline-none transition-colors text-white"
                  placeholder="Project inquiry..."
                  data-testid="contact-subject"
                />
              </div>

              <div>
                <label className="block text-sm text-white/50 mb-2">Your Message</label>
                <textarea
                  required
                  rows={5}
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  className="w-full px-4 py-3 bg-charcoal/50 border border-white/10 focus:border-gold/50 outline-none transition-colors text-white resize-none"
                  placeholder="Tell me about your project..."
                  data-testid="contact-message"
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-gold text-black hover:bg-white transition-colors duration-500 font-bold tracking-widest uppercase text-sm px-8 py-4 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="contact-submit"
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </button>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  return (
    <footer className="py-12 border-t border-white/5">
      <div className="container mx-auto px-6 md:px-12 max-w-7xl">
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="text-center md:text-left">
            <p className="font-serif text-lg text-gold">VIKKI WRIGHT</p>
            <p className="text-sm text-white/40 mt-1">Music Composer & Producer</p>
          </div>

          <div className="flex items-center gap-6">
            {socialLinks.map((social) => (
              <a
                key={social.label}
                href={social.href}
                target="_blank"
                rel="noopener noreferrer"
                className="text-white/40 hover:text-gold transition-colors"
                aria-label={social.label}
              >
                <social.icon className="w-5 h-5" />
              </a>
            ))}
          </div>

          <p className="text-sm text-white/40">
            © {new Date().getFullYear()} Vikki Wright. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};

// Main HomePage Component
function HomePage() {
  const [activeSection, setActiveSection] = useState('home');

  useEffect(() => {
    const handleScroll = () => {
      const sections = ['home', 'about', 'latest-news', 'portfolio', 'studio', 'services', 'featured', 'contact'];
      const scrollPosition = window.scrollY + 200;

      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-void">
      {/* Grain Overlay */}
      <div className="grain-overlay" />
      
      {/* Navigation */}
      <Navigation activeSection={activeSection} />
      
      {/* Main Content */}
      <main>
        <HeroSection />
        <AboutSection />
        <LatestNewsSection />
        <WorkExamplesSection />
        <StudioSection />
        <ServicesSection />
        <FeaturedSection />
        <ContactSection />
      </main>
      
      {/* Footer */}
      <Footer />
    </div>
  );
}

export default HomePage;

